// eslint-disable react-hooks/exhaustive-deps
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { routes } from "../routes/routes";
import { toast } from "react-toastify";
import { Login as LoginAPI } from "../services/auth";
import { LogoutApi } from "../services/logout";
import { GetUserRolesRights } from "../services/admin";
import { useDispatch } from "react-redux";
import { setRolesAndRights } from "../redux/role-rights/Action";
import { GetEpoModulesCounts } from "../services/expense-purchase-order";
import { GetAllDocuments } from "../services/document-management/index";
import {
  GetNotification,
  SetNotificationMode,
  MarkNotificationsSeen,
} from "../services/dashboard";
import EpoNotificationToast from "../components/notifications/EpoNotificationToast";
import { playNotificationSound } from "../utils/notificationSound";
import { Box } from "@mui/material";

interface LoginInterface {
  name: string;
  password: string;
}

interface AuthContextType {
  user: any;
  login: (user: LoginInterface) => void;
  logout: any;
  getEpoModulesCounts: any;
  MPCVoteCount: any;
  mpcAllVoteForEpoCount: any;
  documentAwaitingCount: any;
  getNotifications: any;
  notifications: any[] | null;
  notificationMode: string;
  setNotificationMode: (mode: string) => Promise<void>;
  getAllDocuments: any;
  myApprovalCount: any;
  directorPendingCount: any;
  presidentPendingCount: any;
  mpcDirectorPendingCount: any;
  epoApprovalSidebarCount: any;
  isHigherAuthorityMember: any;
  isPresidentMember: any;
}

const AuthContext =
  React.createContext<AuthContextType>(null!);

const NOTIFICATION_POLL_MS = 10000;
const NOTIFICATION_SESSION_KEY =
  "notificationSessionId";

/*
 * Normal notifications are added to the
 * accordion rapidly.
 */
const NORMAL_BURST_INTERVAL_MS = 120;

const NOTIFICATION_MODES = {
  SOUND: "sound",
  MUTE: "mute",
};

const normalizeNotificationMode = (value: any) => {
  const mode = String(value || "").toLowerCase();

  if (mode === "voice") {
    return NOTIFICATION_MODES.SOUND;
  }

  if (
    mode === NOTIFICATION_MODES.SOUND ||
    mode === NOTIFICATION_MODES.MUTE
  ) {
    return mode;
  }

  return NOTIFICATION_MODES.SOUND;
};

const getNotificationId = (item: any) =>
  Number(item?.id || 0);

const isNotificationSeen = (item: any) =>
  item?.isSeen === true ||
  item?.IsSeen === true ||
  Number(item?.isSeen) === 1 ||
  Number(item?.IsSeen) === 1;

const getBooleanConfig = (
  item: any,
  camelName: string,
  pascalName: string,
  defaultValue = true
) => {
  if (item?.[camelName] !== undefined && item?.[camelName] !== null) {
    return (
      item?.[camelName] === true ||
      Number(item?.[camelName]) === 1
    );
  }

  if (item?.[pascalName] !== undefined && item?.[pascalName] !== null) {
    return (
      item?.[pascalName] === true ||
      Number(item?.[pascalName]) === 1
    );
  }

  return defaultValue;
};

const isNotificationEnabled = (item: any) =>
  getBooleanConfig(
    item,
    "notificationEnabled",
    "NotificationEnabled",
    true
  );

const isPopupEnabled = (item: any) =>
  getBooleanConfig(
    item,
    "popupEnabled",
    "PopupEnabled",
    true
  );

const isSoundEnabled = (item: any) =>
  getBooleanConfig(
    item,
    "soundEnabled",
    "SoundEnabled",
    true
  );

const isPopupCandidate = (item: any) =>
  getNotificationId(item) > 0 &&
  isNotificationEnabled(item) &&
  isPopupEnabled(item) &&
  !isNotificationSeen(item);

const getStoredUser = () => {
  const userObj = localStorage.getItem("user");

  if (!userObj) {
    return null;
  }

  try {
    return JSON.parse(userObj);
  } catch (error) {
    console.error("Stored user data is invalid:", error);
    return null;
  }
};

export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [user, setUser] =
    React.useState<any>(getStoredUser);

  const [MPCVoteCount, setMPCVoteCount] =
    React.useState<any>(0);

  const [myApprovalCount, setMyApprovalCount] =
    React.useState<any>(0);

  const [
    mpcAllVoteForEpoCount,
    setMpcAllVoteForEpoCount,
  ] = React.useState<any>(0);

  const [
    documentAwaitingCount,
    setDocumentAwaitingCount,
  ] = React.useState<any>(0);

  const [
    directorPendingCount,
    setDirectorPendingCount,
  ] = React.useState<any>(0);

  const [
    presidentPendingCount,
    setPresidentPendingCount,
  ] = React.useState<any>(0);

  const [
    mpcDirectorPendingCount,
    setMpcDirectorPendingCount,
  ] = React.useState<any>(0);

  const [
    epoApprovalSidebarCount,
    setEpoApprovalSidebarCount,
  ] = React.useState<any>(0);

  const [
    isHigherAuthorityMember,
    setIsHigherAuthorityMember,
  ] = React.useState<any>(null);

  const [
    isPresidentMember,
    setIsPresidentMember,
  ] = React.useState<any>(null);

  const [notifications, setNotifications] =
    React.useState<any[] | null>(null);

  /*
   * Normal notifications:
   * one accordion group.
   */
  const [normalToastQueue, setNormalToastQueue] =
    React.useState<any[]>([]);

  const [
    notificationMode,
    setNotificationModeState,
  ] = React.useState<string>(
    NOTIFICATION_MODES.SOUND
  );

  /*
   * Notifications known during current login/session.
   *
   * null = first load of this login.
   */
  const knownNotificationIdsRef =
    React.useRef<Set<number> | null>(null);

  const notificationModeRef =
    React.useRef<string>(
      NOTIFICATION_MODES.SOUND
    );

  const notificationSessionIdRef =
    React.useRef<string | null>(
      typeof window !== "undefined"
        ? sessionStorage.getItem(
          NOTIFICATION_SESSION_KEY
        )
        : null
    );

  const pollingInFlightRef =
    React.useRef(false);

  const mountedRef =
    React.useRef(true);

  /*
   * Timers used to create the normal burst.
   */
  const normalBurstTimerRefs =
    React.useRef<number[]>([]);

  /*
   * Used so the normal accordion makes one
   * sound per burst, not one sound for every
   * notification added to the accordion.
   */
  const previousNormalCountRef =
    React.useRef(0);

  /*
   * Prevent repeated MarkNotificationsSeen calls while
   * the same popup group is visible.
   */
  const markSeenInFlightRef =
    React.useRef(false);

  React.useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      normalBurstTimerRefs.current.forEach(
        (timer) => {
          window.clearTimeout(timer);
        }
      );

      normalBurstTimerRefs.current = [];
    };
  }, []);

  React.useEffect(() => {
    if (
      routes.resetPassword.match === location.pathname ||
      location.pathname.includes("view-fto-viaemail")
    ) {
      localStorage.clear();
      sessionStorage.removeItem(NOTIFICATION_SESSION_KEY);
    }
  }, [location?.pathname]);

  const getOrCreateNotificationSession =
    React.useCallback(() => {
      let sessionId =
        notificationSessionIdRef.current;

      if (!sessionId) {
        sessionId =
          crypto.randomUUID();

        notificationSessionIdRef.current =
          sessionId;

        sessionStorage.setItem(
          NOTIFICATION_SESSION_KEY,
          sessionId
        );
      }

      return sessionId;
    }, []);

  const setNotificationMode =
    React.useCallback(
      async (mode: string) => {
        const normalized =
          normalizeNotificationMode(
            mode
          );

        notificationModeRef.current =
          normalized;

        setNotificationModeState(
          normalized
        );

        const response =
          await SetNotificationMode(
            normalized
          );

        if (
          response?.status !== 200
        ) {
          throw new Error(
            "Unable to save notification mode."
          );
        }
      },
      []
    );

  /*
   * =====================================================
   * NORMAL NOTIFICATION BURST
   * =====================================================
   *
   * Notifications are inserted into ONE
   * accordion every 120ms.
   */
  const addNormalNotifications =
    React.useCallback(
      (items: any[]) => {
        if (
          !mountedRef.current ||
          !Array.isArray(items) ||
          items.length === 0
        ) {
          return;
        }

        const sortedItems =
          [...items].sort(
            (a, b) =>
              getNotificationId(a) -
              getNotificationId(b)
          );

        sortedItems.forEach(
          (
            notification,
            index
          ) => {
            const timer =
              window.setTimeout(
                () => {
                  if (
                    !mountedRef.current
                  ) {
                    return;
                  }

                  setNormalToastQueue(
                    (current) => {
                      const id =
                        getNotificationId(
                          notification
                        );

                      if (!id) {
                        return current;
                      }

                      const alreadyExists =
                        current.some(
                          (item) =>
                            getNotificationId(
                              item
                            ) === id
                        );

                      if (
                        alreadyExists
                      ) {
                        return current;
                      }

                      return [
                        ...current,
                        notification,
                      ];
                    }
                  );
                },
                index *
                NORMAL_BURST_INTERVAL_MS
              );

            normalBurstTimerRefs.current.push(
              timer
            );
          }
        );
      },
      []
    );

  /*
   * =====================================================
   * FETCH NOTIFICATIONS
   * =====================================================
   */
  const getNotifications =
    React.useCallback(
      async () => {
        if (pollingInFlightRef.current) {
          return;
        }

        pollingInFlightRef.current = true;

        try {
          const response = await GetNotification();

          if (response?.status !== 200) {
            return;
          }

          const incoming =
            Array.isArray(response?.data)
              ? response.data
              : [];

          /*
           * Load and normalize the saved notification mode.
           * Legacy "voice" is treated as "sound".
           */
          const responseMode =
            incoming.find(
              (item: any) => item?.notificationMode
            )?.notificationMode;

          if (responseMode) {
            const normalized =
              normalizeNotificationMode(responseMode);

            notificationModeRef.current = normalized;

            if (mountedRef.current) {
              setNotificationModeState(normalized);
            }
          }

          if (mountedRef.current) {
            setNotifications(incoming);
          }

          const supportedNotifications =
            incoming.filter(
              (item: any) => getNotificationId(item) > 0
            );

          const incomingIds =
            new Set<number>(
              incoming
                .map((item: any) =>
                  getNotificationId(item)
                )
                .filter((id: number) => id > 0)
            );

          const previousIds =
            knownNotificationIdsRef.current;

          const isFirstLoad =
            previousIds === null;

          /*
           * SQL now controls which notifications are eligible
           * for the popup through NotificationConfig.
           *
           * No module/action numbers are required here.
           * A notification is shown when:
           * - NotificationEnabled = true
           * - PopupEnabled = true
           * - IsSeen = false
           *
           * Approval pending state remains independent and is
           * still used by the NotificationDropdown.
           */
          const popupNotifications =
            supportedNotifications.filter(isPopupCandidate);

          let normalNotificationsToShow: any[] = [];

          if (isFirstLoad) {
            /*
             * On login, show every unseen notification whose
             * SQL configuration allows a popup.
             */
            normalNotificationsToShow =
              popupNotifications;
          } else {
            /*
             * During polling, only brand-new IDs are added
             * to the accordion, and only when SQL says the
             * notification may popup and it is still unseen.
             */
            normalNotificationsToShow =
              popupNotifications.filter(
                (item: any) =>
                  !previousIds?.has(
                    getNotificationId(item)
                  )
              );
          }

          /*
           * Mark this snapshot as known only after the new
           * notification set has been calculated.
           */
          knownNotificationIdsRef.current = incomingIds;

          if (normalNotificationsToShow.length > 0) {
            /*
             * One sound per new notification batch.
             * This also fires when a new notification arrives while
             * the accordion is already open.
             */
            const shouldPlaySound =
              notificationModeRef.current ===
              NOTIFICATION_MODES.SOUND &&
              normalNotificationsToShow.some(
                isSoundEnabled
              );

            if (shouldPlaySound) {
              playNotificationSound().catch(
                (error) => {
                  console.error(
                    "Notification sound failed:",
                    error
                  );
                }
              );
            }

            addNormalNotifications(
              normalNotificationsToShow
            );
          }
        } catch (error) {
          console.error(
            "Notification polling failed:",
            error
          );
        } finally {
          pollingInFlightRef.current = false;
        }
      },
      [addNormalNotifications]
    );

  /*
   * =====================================================
   * INITIAL USER LOAD
   * =====================================================
   */
  React.useEffect(() => {
    const storedUser = getStoredUser();

    if (
      !storedUser
    ) {
      return;
    }

    setUser(
      storedUser
    );

    getOrCreateNotificationSession();

    getEpoModulesCounts();
    getAllDocuments();
    getNotifications();
  }, [
    getOrCreateNotificationSession,
    getNotifications,
  ]);

  /*
   * Existing navigation behavior.
   */
  React.useEffect(() => {
    const userObj =
      localStorage.getItem("user");

    const storedUser =
      userObj
        ? JSON.parse(userObj)
        : null;

    if (!storedUser) {
      return;
    }

    const targetPath =
      location.pathname ||
      routes.dashboard.match;

    const targetUrl =
      location.search
        ? `${targetPath}${location.search}`
        : targetPath;

    if (
      location.pathname !==
      targetPath ||
      location.search
    ) {
      navigate(targetUrl);
    }
  }, [
    location.pathname,
    location.search,
    navigate,
  ]);

  /*
   * Poll every 10 seconds.
   */
  React.useEffect(() => {
    if (!user) {
      return undefined;
    }

    const interval =
      window.setInterval(
        () => {
          getNotifications();
        },
        NOTIFICATION_POLL_MS
      );

    return () =>
      window.clearInterval(
        interval
      );
  }, [
    user,
    getNotifications,
  ]);

  const getAllDocuments =
    async () => {
      const data = {
        data: {
          documentType: null,
          approvalData: true,
          isSendForApproval: true,
        },
        start: 1,
        length: 5,
        sortExpression:
          "id desc",
        searchKeyword: null,
      };

      try {
        const response =
          await GetAllDocuments(
            data
          );

        if (
          response?.status ===
          200
        ) {
          setDocumentAwaitingCount(
            response?.data
              ?.totalRecords
          );
        }
      } catch (
      error
      ) {
        console.error(
          "Document count refresh failed:",
          error
        );
      }
    };

  const getEpoModulesCounts =
    async () => {
      try {
        const response =
          await GetEpoModulesCounts();

        setMPCVoteCount(
          response?.data
            ?.mpcVoteForEpoCount ||
          0
        );

        setMyApprovalCount(
          response?.data
            ?.myApprovalCount ||
          0
        );

        setMpcAllVoteForEpoCount(
          response?.data
            ?.mpcAllVoteForEpoCount ||
          0
        );

        setMpcDirectorPendingCount(
          response?.data
            ?.mpcDirectorPendingCount ||
          0
        );

        setDirectorPendingCount(
          response?.data
            ?.directorPendingCount ||
          0
        );

        setPresidentPendingCount(
          response?.data
            ?.presidentPendingCount ||
          0
        );

        setEpoApprovalSidebarCount(
          response?.data
            ?.epoApprovalSidebarCount ||
          0
        );

        setIsHigherAuthorityMember(
          response?.data
            ?.isHigherAuthorityMember ||
          "N"
        );

        setIsPresidentMember(
          response?.data
            ?.isPresidentMember ||
          "N"
        );
      } catch (
      error
      ) {
        console.error(
          "EPO module count refresh failed:",
          error
        );
      }
    };

  /*
   * =====================================================
   * LOGIN
   * =====================================================
   */
  const login = async (
    newUser: LoginInterface
  ) => {
    const key =
      process.env.REACT_APP_URL;

    try {
      const response =
        await LoginAPI(
          newUser?.name,
          newUser?.password
        );

      if (
        response?.status ===
        200
      ) {
        setUser(
          newUser
        );

        localStorage.setItem(
          "user",
          JSON.stringify(
            newUser
          )
        );

        localStorage.setItem(
          `userToken_${key}`,
          JSON.stringify(
            response?.data
              ?.authToken
          )
        );

        localStorage.setItem(
          "encryptedUserId",
          response?.data
            ?.encryptedUserId
        );

        /*
         * Every real login gets a new
         * approval session.
         */
        const newNotificationSessionId =
          crypto.randomUUID();

        notificationSessionIdRef.current =
          newNotificationSessionId;

        sessionStorage.setItem(
          NOTIFICATION_SESSION_KEY,
          newNotificationSessionId
        );

        /*
         * Reset known IDs.
         *
         * The new login starts a fresh client-side
         * notification snapshot. Already-seen items remain
         * excluded by the SQL-driven seen state.
         */
        knownNotificationIdsRef.current =
          null;

        /*
         * Reset notification queue.
         */
        setNotifications(null);
        setNormalToastQueue([]);

        normalBurstTimerRefs.current.forEach(
          (timer) =>
            window.clearTimeout(
              timer
            )
        );

        normalBurstTimerRefs.current =
          [];

        previousNormalCountRef.current =
          0;

        await getUserRights();

        navigate(
          routes?.dashboard.match,
          { replace: true }
        );

        /*
         * Fallback in case permissions page
         * does not immediately reload.
         */
        window.setTimeout(
          () => {
            getNotifications();
          },
          200
        );
      } else {
        navigate(
          routes?.login.match
        );
      }
    } catch (
    error
    ) {
      toast.error(
        "Username or password is incorrect."
      );
    }
  };

  /*
   * =====================================================
   * LOGOUT
   * =====================================================
   */
  const logout = async () => {
    const response =
      await LogoutApi();

    if (
      response?.status ===
      200
    ) {
      setUser(null);
      setNotifications(null);

      setNormalToastQueue([]);

      notificationSessionIdRef.current =
        null;

      knownNotificationIdsRef.current =
        null;

      normalBurstTimerRefs.current.forEach(
        (timer) =>
          window.clearTimeout(
            timer
          )
      );

      normalBurstTimerRefs.current =
        [];

      previousNormalCountRef.current =
        0;

      sessionStorage.removeItem(
        NOTIFICATION_SESSION_KEY
      );

      localStorage.clear();

      navigate(
        routes?.login?.match
      );

      toast.success(
        "Successfully logout"
      );
    }
  };

  const getUserRights = async () => {
    localStorage.removeItem("userPermissions");

    try {
      const response = await GetUserRolesRights();

      if (response?.status === 200) {
        localStorage.setItem(
          "userPermissions",
          JSON.stringify(response?.data)
        );

        dispatch(
          setRolesAndRights(response?.data)
        );

        return response?.data;
      }

      return null;
    } catch (error) {
      console.error("Get user rights failed:", error);
      return null;
    }
  };

  /*
   * =====================================================
   * CLOSE NORMAL GROUP
   * =====================================================
   */
  const handleNormalToastClose =
    React.useCallback(
      () => {
        setNormalToastQueue([]);

        normalBurstTimerRefs.current.forEach(
          (timer) =>
            window.clearTimeout(
              timer
            )
        );

        normalBurstTimerRefs.current =
          [];

        previousNormalCountRef.current =
          0;
      },
      []
    );

  const handleToastActionComplete =
    React.useCallback(
      async () => {
        await getNotifications();
        await getEpoModulesCounts();
      },
      [getNotifications]
    );

  /*
   * =====================================================
   * MARK POPUP NOTIFICATIONS SEEN
   * =====================================================
   *
   * As soon as the notification accordion becomes visible,
   * acknowledge the current popup notifications.
   *
   * All popup notifications are acknowledged by
   * emp.MarkNotificationsSeen.
   *
   * Marking ActionType 6 as seen does NOT complete the approval.
   * IsApprovalPending remains independent, so the approval still
   * remains inside the Approval Required bell list until the
   * actual approval/denial is completed.
   */
  React.useEffect(() => {
    const currentCount =
      normalToastQueue.length;

    const previousCount =
      previousNormalCountRef.current;

    const popupJustStarted =
      previousCount === 0 &&
      currentCount > 0;

    if (
      !popupJustStarted ||
      markSeenInFlightRef.current
    ) {
      return;
    }

    markSeenInFlightRef.current = true;

    MarkNotificationsSeen()
      .catch((error) => {
        console.error(
          "Mark notification popup items seen failed:",
          error
        );
      })
      .finally(() => {
        markSeenInFlightRef.current = false;
      });
  }, [normalToastQueue.length]);

  /*
   * Keep the popup visibility count ref synchronized.
   * Sound is handled in getNotifications() so a new polling
   * batch can still produce a sound while the popup is open.
   */
  React.useEffect(() => {
    previousNormalCountRef.current =
      normalToastQueue.length;
  }, [normalToastQueue.length]);

  const value = {
    user,
    login,
    logout,
    getEpoModulesCounts,
    getNotifications,
    notifications,
    notificationMode,
    setNotificationMode,
    MPCVoteCount,
    myApprovalCount,
    mpcAllVoteForEpoCount,
    documentAwaitingCount,
    directorPendingCount,
    presidentPendingCount,
    isHigherAuthorityMember,
    isPresidentMember,
    getAllDocuments,
    mpcDirectorPendingCount,
    epoApprovalSidebarCount,
  };

  /*
   * =====================================================
   * NOTIFICATION OVERLAY
   * =====================================================
   *
   * All notification types use one small accordion.
   * Approval actions remain available in the Notification
   * Dropdown > Approval Required tab.
   */
  return (
    <AuthContext.Provider value={value}>
      {children}

      {normalToastQueue.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            top: {
              xs: 12,
              sm: 20,
            },
            left: "50%",
            transform: "translateX(-50%)",
            width: {
              xs: "calc(100vw - 24px)",
              sm: 430,
            },
            zIndex: (theme) =>
              theme.zIndex.modal + 50,
          }}
        >
          <EpoNotificationToast
            notifications={normalToastQueue}
            onClose={handleNormalToastClose}
            onActionComplete={
              handleToastActionComplete
            }
            isNormalGroup
          />
        </Box>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return React.useContext(
    AuthContext
  );
}
